import { css } from 'aphrodite';
import * as React from 'react';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import { isValidEmail } from '../../../models/utils/validationUtils';
import { useChangeCustomerMutation } from '../../../queries/Support/useSupportTicketMutations';
import { useSupportUserSearchQuery } from '../../../queries/Support/useSupportUserSearch';
import { getApiErrorMessage, getInactiveUserStatusBadge } from '../../../utils/Support/supportHelpers';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Button } from '../../../web/components-v2/Button';
import { Dialog, DialogFooter, DialogHeader, DialogPopup, DialogTitle } from '../../../web/components-v2/Dialog';
import { Input } from '../../../web/components-v2/Input';
import { Pill } from '../../../web/components-v2/Pill';
import { supportSharedStyles as shared } from '../../components/supportShared/styles/supportSharedStyles';
import { styles } from './styles';

interface ChangeCustomerModalProps {
	ticketId: string;
	hasCustomer?: boolean;
	onClose: () => void;
}

/** Modal used by support agents to assign a ticket to a known user or a manual contact. */
export const ChangeCustomerModal: React.FC<ChangeCustomerModalProps> = ({ ticketId, hasCustomer = true, onClose }) => {
	const [search, setSearch] = React.useState('');
	const debouncedSearch = useDebounceValue(search, 300);
	const userSearchQuery = search.trim().length < 2 ? '' : debouncedSearch;
	const [showManual, setShowManual] = React.useState(false);
	const [email, setEmail] = React.useState('');
	const [name, setName] = React.useState('');
	const [error, setError] = React.useState('');
	const changeCustomer = useChangeCustomerMutation(ticketId);
	const action = hasCustomer ? 'change' : 'add';
	const actionLabel = hasCustomer ? 'Change Customer' : 'Add Customer';
	const failureMessage = `Failed to ${action} customer. Please try again.`;

	const searchResults = useSupportUserSearchQuery(userSearchQuery);
	const results = searchResults.data ?? [];

	const handleSelectUser = (userId: string) => {
		setError('');
		changeCustomer.mutate(
			{ userId },
			{
				onSuccess: () => onClose(),
				onError: err => setError(getApiErrorMessage(err, failureMessage)),
			}
		);
	};

	const handleManualSubmit = () => {
		const trimmedEmail = email.trim();
		if (!trimmedEmail) {
			setError('Email is required');
			return;
		}
		if (!isValidEmail(trimmedEmail)) {
			setError('Please enter a valid email address');
			return;
		}
		setError('');
		changeCustomer.mutate(
			{
				contactEmail: trimmedEmail,
				contactName: name.trim() || undefined,
			},
			{
				onSuccess: () => onClose(),
				onError: err => setError(getApiErrorMessage(err, failureMessage)),
			}
		);
	};

	return (
		<Dialog
			open
			onOpenChange={open => {
				if (!open) onClose();
			}}
		>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>{actionLabel}</DialogTitle>
				</DialogHeader>
				<div className={css(shared.modalBody)}>
					{!showManual ? (
						<>
							<Input
								styles={[shared.modalInput]}
								type='text'
								placeholder='Search by name or email...'
								value={search}
								onChange={e => setSearch(e.target.value)}
								autoFocus
							/>
							{userSearchQuery && searchResults.isLoading && (
								<div className={css(shared.modalSpinnerRow)}>
									<LoadingSpinner type='small' />
								</div>
							)}
							{results.length > 0 && (
								<div className={css(styles.linkTicketResults)}>
									{results.map(user => {
										const displayName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
										const statusBadge = getInactiveUserStatusBadge(user.activationStatus);

										return (
											<Button
												key={user.userId}
												type='button'
												variant='ghost'
												styles={[styles.linkTicketResult]}
												onClick={() => handleSelectUser(user.userId)}
												disabled={changeCustomer.isPending}
											>
												<div className={css(styles.changeCustomerResultLeft)}>
													<div className={css(styles.changeCustomerResultIdentity)}>
														<div
															className={css(styles.linkTicketResultNumber, styles.changeCustomerResultIdentityText)}
														>
															{displayName || user.email}
														</div>
														{statusBadge && (
															<Pill variant='label' color={statusBadge.color} size='small' label={statusBadge.label} />
														)}
													</div>
													{displayName && <div className={css(styles.changeCustomerResultEmail)}>{user.email}</div>}
												</div>
												<span className={css(styles.linkTicketResultContact)}>{user.accountName}</span>
											</Button>
										);
									})}
								</div>
							)}
							{userSearchQuery && !searchResults.isLoading && results.length === 0 && (
								<div className={css(shared.modalErrorText)}>No users found</div>
							)}
							{error && <div className={css(shared.modalErrorText)}>{error}</div>}
							<Button variant='link' styles={[styles.changeCustomerManualLink]} onClick={() => setShowManual(true)}>
								Manual entry
							</Button>
						</>
					) : (
						<>
							<div>
								<label className={css(shared.modalLabel)}>Email (required)</label>
								<Input
									styles={[shared.modalInput, error && shared.modalInputError]}
									type='email'
									placeholder='customer@example.com'
									value={email}
									onChange={e => {
										setEmail(e.target.value);
										if (error) setError('');
									}}
									onKeyDown={e => {
										if (e.key === 'Enter') handleManualSubmit();
									}}
									autoFocus
								/>
								{error && <div className={css(shared.modalErrorText)}>{error}</div>}
							</div>
							<div>
								<label className={css(shared.modalLabel)}>Name (optional)</label>
								<Input
									styles={[shared.modalInput]}
									type='text'
									placeholder='John Doe'
									value={name}
									onChange={e => setName(e.target.value)}
									onKeyDown={e => {
										if (e.key === 'Enter') handleManualSubmit();
									}}
								/>
							</div>
							<Button variant='link' styles={[styles.changeCustomerManualLink]} onClick={() => setShowManual(false)}>
								Back to search
							</Button>
						</>
					)}
				</div>
				<DialogFooter>
					<Button variant='outline' size='sm' onClick={onClose}>
						Cancel
					</Button>
					{showManual && (
						<Button variant='primary' size='sm' onClick={handleManualSubmit} disabled={changeCustomer.isPending}>
							{changeCustomer.isPending ? (hasCustomer ? 'Changing...' : 'Adding...') : actionLabel}
						</Button>
					)}
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
